import {ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {FuseAlertType} from '@fuse/components/alert';
import {fuseAnimations} from '@fuse/animations';
import {AccountRole} from '@enums/AccountRole';
import {User} from '@models/user.interface';
import {UserService} from '@core/user/user.service';
import {PaymentBlockService} from '@services/payment-block.service';
import {MatSlideToggle, MatSlideToggleChange} from '@angular/material/slide-toggle';
import {MatDialog} from '@angular/material/dialog';
import {PaymentBlockReasonDialogComponent} from '@shared/components/payment-block-reason-dialog/payment-block-reason-dialog.component';

@Component({
    selector: 'app-payment-block-switch',
    templateUrl: './payment-block-switch.component.html',
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PaymentBlockSwitchComponent implements OnInit, OnChanges, OnDestroy {

    @ViewChild(MatSlideToggle) private readonly _slideToggle: MatSlideToggle;

    @Input() hotelId: number;
    @Input() companyId: number;

    paymentBlocked: boolean = false;
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    canToggle: boolean = false;

    alert: { type: FuseAlertType; message: string } = {type: 'info', message: ''};

    private _showAlert: boolean = false;
    private _submittingTimeout: ReturnType<typeof setTimeout> | null = null;
    private readonly _unsubscribeAll$: Subject<any> = new Subject<any>();

    constructor(
        private readonly _userService: UserService,
        private readonly _paymentBlockService: PaymentBlockService,
        private readonly _dialog: MatDialog,
        private readonly _ngZone: NgZone,
        private readonly _cdr: ChangeDetectorRef
    ) {}

    get showAlert(): boolean {
        return this._showAlert;
    }

    set showAlert(val: boolean) {
        this._showAlert = val;
        if (val) {
            setTimeout((): void => {
                this._showAlert = false;
            }, 5000);
        }
    }

    ngOnInit(): void {
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((user: User): void => {
            this.canToggle = user?.rol === AccountRole.master
                || user?.rol === AccountRole.semiMaster
                || user?.rol === AccountRole.adminHotel
                || user?.rol === AccountRole.accountant;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['hotelId'] || changes['companyId']) && this.hotelId && this.companyId) {
            this._loadPaymentBlock();
        }
    }

    private _loadPaymentBlock(): void {
        this.isLoading = true;
        this._cdr.detectChanges();
        this._paymentBlockService.getPaymentBlock(this.hotelId, this.companyId)
            .pipe(takeUntil(this._unsubscribeAll$))
            .subscribe({
                next: (response): void => {
                    this.paymentBlocked = response?.paymentBlocked ?? false;
                    this.isLoading = false;
                    this._cdr.detectChanges();
                },
                error: (): void => {
                    this.isLoading = false;
                    this._cdr.detectChanges();
                }
            });
    }

    ngOnDestroy(): void {
        if (this._submittingTimeout !== null) {
            clearTimeout(this._submittingTimeout);
        }
        this._unsubscribeAll$.next(null);
        this._unsubscribeAll$.complete();
    }

    onToggleChange(event: MatSlideToggleChange): void {
        if (this.isSubmitting) return;
        // Guard: mat-slide-toggle puede disparar (change) en actualizaciones programáticas
        if (event.checked === this.paymentBlocked) return;

        if (event.checked) {
            const dialogRef = this._dialog.open(PaymentBlockReasonDialogComponent, {
                width: '480px',
                disableClose: true
            });
            dialogRef.afterClosed().pipe(takeUntil(this._unsubscribeAll$)).subscribe((reason: string | null): void => {
                if (!reason) {
                    this.paymentBlocked = false;
                    if (this._slideToggle) {
                        this._slideToggle.checked = false;
                    }
                    return;
                }
                this._submitPaymentBlock(true, reason);
            });
        } else {
            this._submitPaymentBlock(false, null);
        }
    }

    private _clearSubmittingTimeout(): void {
        if (this._submittingTimeout !== null) {
            clearTimeout(this._submittingTimeout);
            this._submittingTimeout = null;
        }
    }

    private _submitPaymentBlock(paymentBlocked: boolean, reason: string | null): void {
        this.paymentBlocked = paymentBlocked;
        this.isSubmitting = true;
        this.showAlert = false;

        this._submittingTimeout = setTimeout((): void => {
            this._ngZone.run((): void => {
                this.isSubmitting = false;
                this.alert = {type: 'info', message: 'La operación está siendo procesada. Los cambios se reflejarán en breve.'};
                this.showAlert = true;
                this._cdr.detectChanges();
            });
        }, 30_000);

        this._paymentBlockService.updatePaymentBlock(this.hotelId, this.companyId, paymentBlocked, reason)
            .pipe(takeUntil(this._unsubscribeAll$))
            .subscribe({
                next: (response): void => {
                    this._clearSubmittingTimeout();
                    this.isSubmitting = false;
                    this.paymentBlocked = response?.paymentBlocked ?? paymentBlocked;
                    this.alert = {
                        type: 'success',
                        message: this.paymentBlocked
                            ? 'Pago bloqueado correctamente.'
                            : 'Pago habilitado correctamente.'
                    };
                    this.showAlert = true;
                    this._cdr.detectChanges();
                },
                error: (): void => {
                    this._clearSubmittingTimeout();
                    this.isSubmitting = false;
                    this.paymentBlocked = !paymentBlocked;
                    this.alert = {type: 'error', message: 'Ocurrió un error. Inténtalo de nuevo.'};
                    this.showAlert = true;
                    this._cdr.detectChanges();
                }
            });
    }
}
